
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Calendar, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import type { ResultadoFormData } from "@/types/softwareTurbinado";

type MultiStepResultadoModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (resultados: ResultadoFormData[]) => Promise<boolean>;
  isLoading: boolean;
  selectedDays: number;
  titulos: {
    ppt: string;
    ptm: string;
    pt: string;
    ptv: string;
    ptn: string;
    coruja: string;
  };
};

const MultiStepResultadoModal = ({ 
  open, 
  onClose, 
  onSave, 
  isLoading, 
  selectedDays,
  titulos
}: MultiStepResultadoModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [resultados, setResultados] = useState<ResultadoFormData[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      // Inicializar array de resultados baseado nos dias selecionados
      const initialResultados: ResultadoFormData[] = [];
      for (let i = 0; i < selectedDays; i++) {
        const today = new Date();
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() - i);
        
        initialResultados.push({
          data: targetDate.toISOString().split('T')[0],
          ppt_1: '', ppt_2: '', ppt_3: '', ppt_4: '', ppt_5: '',
          ptm_1: '', ptm_2: '', ptm_3: '', ptm_4: '', ptm_5: '',
          pt_1: '', pt_2: '', pt_3: '', pt_4: '', pt_5: '',
          ptv_1: '', ptv_2: '', ptv_3: '', ptv_4: '', ptv_5: '',
          ptn_1: '', ptn_2: '', ptn_3: '', ptn_4: '', ptn_5: '',
          coruja_1: '', coruja_2: '', coruja_3: '', coruja_4: '', coruja_5: ''
        });
      }
      setResultados(initialResultados);
      setCurrentStep(0);
    }
  }, [open, selectedDays]);

  const updateResultado = (index: number, field: keyof ResultadoFormData, value: string) => {
    setResultados(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleNext = () => {
    if (currentStep < selectedDays - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    // Validar se todos os campos obrigatórios estão preenchidos
    const hasEmptyFields = resultados.some(resultado => 
      !resultado.data ||
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].some(field => !field || field.length !== 4)
    );

    if (hasEmptyFields) {
      toast.error("Por favor, preencha todos os campos com números de 4 dígitos");
      return;
    }

    const success = await onSave(resultados);
    if (success) {
      onClose();
    }
  };

  const clearAll = () => {
    setResultados(prev => prev.map(resultado => ({
      ...resultado,
      ppt_1: '', ppt_2: '', ppt_3: '', ppt_4: '', ppt_5: '',
      ptm_1: '', ptm_2: '', ptm_3: '', ptm_4: '', ptm_5: '',
      pt_1: '', pt_2: '', pt_3: '', pt_4: '', pt_5: '',
      ptv_1: '', ptv_2: '', ptv_3: '', ptv_4: '', ptv_5: '',
      ptn_1: '', ptn_2: '', ptn_3: '', ptn_4: '', ptn_5: '',
      coruja_1: '', coruja_2: '', coruja_3: '', coruja_4: '', coruja_5: ''
    })));
  };

  const renderHorarioSection = (horario: string, titulo: string, bgColor: string, textColor: string = 'text-white') => {
    return (
      <div key={horario} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className={`${bgColor} ${textColor} text-center py-1 font-bold text-xs`}>
          {titulo}
        </div>
        <div className={`${isMobile ? 'p-2 space-y-2' : 'p-1 space-y-1'}`}>
          {[1, 2, 3, 4, 5].map((num) => {
            const fieldName = `${horario}_${num}` as keyof ResultadoFormData;
            return (
              <div key={num} className="flex items-center gap-1">
                <div className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0`}>
                  {num}
                </div>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={resultados[currentStep]?.[fieldName] || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    updateResultado(currentStep, fieldName, value);
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0000"
                  maxLength={4}
                  autoComplete="off"
                  className={`text-center font-mono text-xs flex-1 ${isMobile ? 'h-10' : 'h-7'}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!open) return null;

  const progressPercentage = ((currentStep + 1) / selectedDays) * 100;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`
        ${isMobile ? 'w-[95vw] h-[95vh] max-w-none max-h-none' : 'max-w-5xl max-h-[85vh]'} 
        overflow-y-auto fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2
      `}>
        <DialogHeader className="pb-2">
          <DialogTitle className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-center`}>
            Inserir Resultados - Dia {currentStep + 1} de {selectedDays}
          </DialogTitle>
          
          {/* Barra de Progresso */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          
          {/* Botões de Ação */}
          <div className="flex justify-between items-center mt-2 gap-2">
            <Button
              variant="outline"
              onClick={clearAll}
              className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50 text-xs px-2"
            >
              <Trash2 className="h-3 w-3" />
              Limpar Tudo
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const progressSaved = toast.success("Progresso salvo!");
                setTimeout(() => progressSaved, 2000);
              }}
              className="flex items-center gap-2 text-green-600 border-green-300 hover:bg-green-50 text-xs px-2"
            >
              <Save className="h-3 w-3" />
              Salvar Progresso
            </Button>
          </div>
        </DialogHeader>

        {/* Data do Sorteio */}
        <div className={`bg-blue-50 ${isMobile ? 'p-3 mb-4' : 'p-2 mb-3'} rounded-lg`}>
          <Label htmlFor={`data-${currentStep}`} className="text-sm font-medium mb-2 block flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Data do Sorteio - Dia {currentStep + 1}
          </Label>
          <Input
            id={`data-${currentStep}`}
            type="date"
            value={resultados[currentStep]?.data || ''}
            onChange={(e) => updateResultado(currentStep, 'data', e.target.value)}
            autoComplete="off"
            className="max-w-xs"
          />
        </div>

        {/* Layout em Grade 3x2 - Compacto para Desktop */}
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-2 mb-4' : 'grid-cols-3 gap-2 mb-3'}`}>
          {/* Primeira linha: PPT, PTM, PT */}
          {renderHorarioSection('ppt', titulos.ppt, 'bg-blue-500')}
          {renderHorarioSection('ptm', titulos.ptm, 'bg-green-500')}
          {!isMobile && renderHorarioSection('pt', titulos.pt, 'bg-red-500')}
        </div>

        {isMobile && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {renderHorarioSection('pt', titulos.pt, 'bg-red-500')}
            {renderHorarioSection('ptv', titulos.ptv, 'bg-purple-500')}
          </div>
        )}

        <div className={`grid ${isMobile ? 'grid-cols-2 gap-2 mb-4' : 'grid-cols-3 gap-2 mb-3'}`}>
          {/* Segunda linha: PTV, PTN, CORUJA */}
          {!isMobile && renderHorarioSection('ptv', titulos.ptv, 'bg-purple-500')}
          {renderHorarioSection('ptn', titulos.ptn, 'bg-orange-500')}
          {renderHorarioSection('coruja', titulos.coruja, 'bg-gray-500')}
        </div>

        {/* Mensagem de Validação */}
        <div className={`bg-red-50 border border-red-200 rounded-lg ${isMobile ? 'p-3 mb-4' : 'p-2 mb-3'}`}>
          <p className="text-red-600 text-xs text-center">
            Por favor, preencha a data e todos os resultados do {titulos.ppt} para continuar.
          </p>
        </div>

        {/* Navegação */}
        <div className="flex justify-between items-center pt-2 border-t gap-2">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center gap-2 text-xs px-2"
          >
            <ChevronLeft className="h-3 w-3" />
            Voltar
          </Button>

          <div className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded">
            {currentStep + 1} de {selectedDays}
          </div>

          {currentStep < selectedDays - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2 text-xs px-2"
            >
              Próximo
              <ChevronRight className="h-3 w-3" />
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2 text-xs px-2"
            >
              <Save className="h-3 w-3" />
              {isLoading ? "Salvando..." : "Salvar Todos"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MultiStepResultadoModal;
